import { useAsyncData } from 'nuxt/app'
import { computed, ref } from 'vue'
import { useAuthStore } from '~/stores/authStore'
import { useSecurityStore } from '~/stores/securityStore'
import { useToastStore } from '~/stores/toastStore'
import type {
  AgenticProduct,
  FilterCompareRequest,
  FilterCompareResponse,
  FilterQueryRequest,
  FilterQueryResponse
} from '~~/shared/types/common'
import { URL_CONFIG } from '~~/shared/utils/url.const'

const MAX_PRODUCT_SKUS = 25
const MAX_COMPARE_SKUS = 5
const AGENTIC_FILTER_STORAGE_KEY = 'agenticfilterobject'

type FilterOperation = 'ADD' | 'REVERT'

const DEFAULT_ERROR_MESSAGE = 'Unable to update filters right now. Please try again.'

const EMPTY_FILTER_QUERY_RESPONSE: FilterQueryResponse = {
  filter_state: '',
  content: {
    title: '',
    short_description: ''
  },
  products: [],
  best_match_product: null,
  product_count: 0,
  facet_filters: [],
  is_compare: false
}

const EMPTY_COMPARE_RESPONSE: FilterCompareResponse = {
  title: '',
  short_description: '',
  recommended_sku: '',
  products: [],
  comparison: []
}

type AgenticInitContext = {
  filter_id: string
  filter_state: string
  content: {
    title: string
    short_description: string
  }
  facet_filters: {
    filter_name: string
    display_name: string
  }[]
}

type AgenticFilterStorage = {
  filter_id?: string
  filter_state?: string
  previous_filter_state?: string
  agenticfilters?: string[]
  filterkey?: string
}

const normalizeFilterValue = (filter: string) => {
  try {
    return decodeURIComponent(filter.replace(/\+/g, ' ')).trim()
  } catch {
    return filter.replace(/\+/g, ' ').trim()
  }
}

const normalizeFilterList = (filters: string[]) => {
  return [...new Set(filters
    .flatMap(filter => filter.split(','))
    .map(normalizeFilterValue)
    .filter(Boolean))]
}

const isSameFilterList = (a: string[], b: string[]) => (
  a.length === b.length && a.every((filter, index) => filter === b[index])
)

export const useAgenticProductQuery = (
  initialFilterId: string,
  initialFilterState = '',
  initialFilterKey = '',
  initialAppliedFilterKeys: string[] = []
) => {
  const authStore = useAuthStore()
  const securityStore = useSecurityStore()
  const toastStore = useToastStore()
  const config = useRuntimeConfig()

  const getAgenticFilterStorage = (): AgenticFilterStorage | null => {
    if (!process.client) return null
    try {
      const stored = localStorage.getItem(AGENTIC_FILTER_STORAGE_KEY)
      if (!stored) return null
      const parsed = JSON.parse(stored)
      if (!parsed || typeof parsed !== 'object') {
        return null
      }
      return parsed as AgenticFilterStorage
    } catch (error) {
      return null
    }
  }
  const storedFilter = getAgenticFilterStorage()

  const resolvedInitialFilterId = storedFilter?.filter_id || initialFilterId || ''
  const resolvedInitialFilterState = storedFilter?.filter_state || initialFilterState || ''
  const resolvedInitialFilterKey = storedFilter?.filterkey || initialFilterKey || ''
  const resolvedInitialAppliedFilters = storedFilter?.agenticfilters?.length ? normalizeFilterList(storedFilter.agenticfilters) : normalizeFilterList(initialAppliedFilterKeys)

  const initial = structuredClone(EMPTY_FILTER_QUERY_RESPONSE)
  const response = ref<FilterQueryResponse>(initial)
  const activeFilterId = ref(resolvedInitialFilterId)
  const filterState = ref( resolvedInitialFilterState || initial.filter_state)
  const activeFilterKey = ref(normalizeFilterValue(resolvedInitialFilterKey))
  const appliedFilters = ref<string[]>(resolvedInitialAppliedFilters)
  const hasQueryResponse = ref(false)
  const apiError = ref<string | null>(null)
  const compareApiError = ref<string | null>(null)
  const pendingOperation = ref<FilterOperation>('ADD')
  const pendingAppliedFilters = ref<string[]>([])
  const pendingCompareSkus = ref<string[]>([])
  const pendingCustomerId = ref('')
  const compareResponse = ref<FilterCompareResponse>(structuredClone(EMPTY_COMPARE_RESPONSE))
  const hasCompareResponse = ref(false)
  const shouldReplayPreviousState = ref(!!storedFilter?.previous_filter_state)

  const hasValidFilterId = computed(() => !!activeFilterId.value)
  const updateAgenticFilterStorage = (
    overrides: Partial<AgenticFilterStorage> = {}) => {
    if (!process.client) return
    const current = getAgenticFilterStorage() ?? {}
    const next: AgenticFilterStorage = {
      ...current,
      filter_id: overrides.filter_id ?? activeFilterId.value,
      filter_state: overrides.filter_state ?? filterState.value,
      previous_filter_state: overrides.previous_filter_state ?? current.previous_filter_state,
      agenticfilters: overrides.agenticfilters ?? appliedFilters.value,
      filterkey: overrides.filterkey ?? activeFilterKey.value
    }
    localStorage.setItem(AGENTIC_FILTER_STORAGE_KEY,JSON.stringify(next))
  }
  const syncFromAgenticFilterStorage = () => {
    if (!process.client) return
    const stored = getAgenticFilterStorage()
    if (!stored) return
    if (stored.filter_id) {
      activeFilterId.value = stored.filter_id
    }
    if (stored.filter_state) {
      filterState.value = stored.filter_state
    }
    if (stored.filterkey) {
      activeFilterKey.value = normalizeFilterValue(
        stored.filterkey
      )
    }
    if (Array.isArray(stored.agenticfilters)) {
      appliedFilters.value = normalizeFilterList(
        stored.agenticfilters
      )
    }
  }

  const getHeaders = async () => {
    await authStore.ensureSession()
    if (!securityStore.data?.csrf) {
      await securityStore.fetch()
    }

    const csrf = securityStore.data?.csrf
    if (!csrf) {
      throw new Error('Invalid CSRF token/data.')
    }

    return {
      Authorization: `Bearer ${authStore.token}`,
      'Session-Id': authStore.sessionId,
      [csrf.headerName]: csrf.token
    }
  }

  const normalizeProductSkus = (products: AgenticProduct[]): string[] => {
    const skus = products
      .map(product => product?.sku)
      .filter((sku): sku is string => typeof sku === 'string' && sku.trim().length > 0)

    return [...new Set(skus)].slice(0, MAX_PRODUCT_SKUS)
  }

  const getErrorCode = (err: any): string | null => {
    return (
      err?.data?.errorCode ||
      err?.data?.code ||
      err?.data?.error?.code ||
      err?.response?._data?.errorCode ||
      err?.response?._data?.code ||
      null
    )
  }

  const getErrorMessage = (status: number, code: string | null): string => {
    if (status === 409 && code === 'STALE_FILTER_STATE') {
      return 'Your filter state is out of date. Please restart your filter selection.'
    }

    if (status === 401) return 'Your session expired. Please try again.'
    if (status === 403) return 'You do not have access to this filter.'
    if (status === 429) return 'Too many requests. Please wait and try again.'
    if (status === 410) return 'This filter is no longer available.'

    return DEFAULT_ERROR_MESSAGE
  }

  const applyNextState = (next: FilterQueryResponse) => {
    response.value = next
    if (next.filter_state) {
      filterState.value = next.filter_state
    }
    updateAgenticFilterStorage({
      filter_id: activeFilterId.value,
      filter_state: next.filter_state || filterState.value,
      agenticfilters: appliedFilters.value,
      filterkey: activeFilterKey.value
    })
  }

  const setInitContext = (next: AgenticInitContext | null | undefined) => {
    if (!next) return

    activeFilterId.value = next.filter_id || activeFilterId.value

    const merged: FilterQueryResponse = {
      ...response.value,
      filter_state: next.filter_state || response.value.filter_state,
      content: next.content || response.value.content,
      facet_filters: next.facet_filters || response.value.facet_filters
    }

    applyNextState(merged)
    updateAgenticFilterStorage({
      filter_id: activeFilterId.value,
      filter_state: merged.filter_state,
      agenticfilters: appliedFilters.value,
      filterkey:
        activeFilterKey.value ||
        normalizeFilterValue(initialFilterKey)
    })
  }

  const mergeWithCurrentState = (
    apiResponse?: Partial<FilterQueryResponse> | null
  ): FilterQueryResponse => {
    return {
      ...response.value,
      ...(apiResponse || {}),
      filter_state: apiResponse?.filter_state || filterState.value,
      content: apiResponse?.content || response.value.content,
      facet_filters: apiResponse?.facet_filters || response.value.facet_filters,
      products: apiResponse?.products || response.value.products,
      product_count: apiResponse?.product_count ?? response.value.product_count,
      best_match_product: apiResponse?.best_match_product ?? null,
      is_compare: apiResponse?.is_compare ?? response.value.is_compare
    }
  }

  const getNextAppliedFilters = (filterKey: string | undefined, operation: FilterOperation): string[] => {
    const nextFilters = [...appliedFilters.value]
    const normalizedFilterKey = filterKey ? normalizeFilterValue(filterKey) : ''

    if (operation === 'ADD' && normalizedFilterKey) {
      return normalizeFilterList([
        ...nextFilters.filter(filter => normalizeFilterValue(filter) !== normalizedFilterKey),
        normalizedFilterKey
      ])
    } else if (normalizedFilterKey) {
      const idx = nextFilters.lastIndexOf(normalizedFilterKey)
      if (idx > -1) nextFilters.splice(idx, 1)
    }

    return normalizeFilterList(nextFilters)
  }

  const getFilterQuery = (filters: string[]): string => {
    return normalizeFilterList(filters).join(',')
  }

  const fetchFilterQuery = async (): Promise<FilterQueryResponse | null> => {
    syncFromAgenticFilterStorage()
    const stored = getAgenticFilterStorage()

    const resolvedFilterId = stored?.filter_id || activeFilterId.value || initialFilterId || ''
    const resolvedFilterState = (
      shouldReplayPreviousState.value
        ? stored?.previous_filter_state
        : stored?.filter_state
    ) || filterState.value || initialFilterState || ''
    const resolvedFilterKey = stored?.filterkey || activeFilterKey.value || normalizeFilterValue(initialFilterKey)
    const resolvedAppliedFilters = stored?.agenticfilters?.length ? normalizeFilterList(stored.agenticfilters) : normalizeFilterList(pendingAppliedFilters.value)
    activeFilterId.value = resolvedFilterId
    filterState.value = resolvedFilterState
    activeFilterKey.value = resolvedFilterKey
    if (stored?.agenticfilters?.length) {
        appliedFilters.value =
        resolvedAppliedFilters
    }

    const operation =
      pendingOperation.value

    if (!resolvedFilterId || !resolvedFilterState || !resolvedFilterKey) {
      const reason = !resolvedFilterId ? 'missing filter_id from localStorage/route' : !resolvedFilterState ? 'missing filter_state from localStorage/route' : 'missing filter_key from localStorage/route'

      const detail = {
        reason,
        filterKey: resolvedFilterKey,
        operation,
        activeFilterId: resolvedFilterId,
        filterState: resolvedFilterState,
        activeFilterKey: resolvedFilterKey,
        agenticFilters: resolvedAppliedFilters
      }


      apiError.value = `Query API skipped: ${reason}`
      return null
    }

    try {
      apiError.value = null
      await authStore.ensureSession()

      const filterQuery = getFilterQuery(resolvedAppliedFilters)
      const filterKeyvalue = resolvedFilterKey || filterQuery.slice(filterQuery.lastIndexOf(',') + 1)
      const filterKey = filterKeyvalue
        ? normalizeFilterValue(filterKeyvalue)
        : filterQuery.slice(filterQuery.lastIndexOf(',') + 1) || resolvedFilterKey
      const payload: FilterQueryRequest = {
        filter_id: resolvedFilterId,
        filter_state:resolvedFilterState,
        session_id:authStore.sessionId,
        filter_key:filterKey,
        filter_query: null,
        product_skus:normalizeProductSkus(response.value.products),
        operation,
        stream: false
      }

      const siteFetch = useNuxtApp().$siteFetch as <T>(
        url: string,
        options: {
          method: string
          body: FilterQueryRequest
          headers: Record<string, string>
          credentials: 'include'
        }
      ) => Promise<T>

      const apiResponse = await siteFetch<FilterQueryResponse | Partial<FilterQueryResponse>>(`${config.public.tgptBaseUrl}${URL_CONFIG.AGENTIC_FILTER_QUERY_ENDPOINT}`, {
        method: 'POST',
        body: payload,
        headers: await getHeaders(),
        credentials: 'include'
      })

      const next = mergeWithCurrentState(apiResponse as Partial<FilterQueryResponse>)

      response.value = next

      filterState.value = next.filter_state || resolvedFilterState
      appliedFilters.value = resolvedAppliedFilters
      hasQueryResponse.value = true
      shouldReplayPreviousState.value = false
      updateAgenticFilterStorage({filter_id:resolvedFilterId,filter_state:filterState.value,previous_filter_state:resolvedFilterState,agenticfilters:resolvedAppliedFilters,filterkey:filterKey})

      return next
    } catch (err: any) {
      const status = err?.statusCode || err?.response?.status || err?.status || 500
      const code = getErrorCode(err)
      const message = getErrorMessage(status, code)

      console.error('[useAgenticProductQuery] query API failed', {
        status,
        code,
        filterKey: resolvedFilterKey,
        operation,
        activeFilterId: activeFilterId.value,
        filterState: filterState.value,
        endpoint: `${config.public.tgptBaseUrl}${URL_CONFIG.AGENTIC_FILTER_QUERY_ENDPOINT}`
      })

      apiError.value = message
      toastStore.publish(message, 'error')
      throw err
    }
  }


  const fetchCompareQuery = async (): Promise<FilterCompareResponse | null> => {
    syncFromAgenticFilterStorage()
    const stored = getAgenticFilterStorage()

    const resolvedFilterThread = stored?.filter_id || activeFilterId.value || initialFilterId || ''
    const resolvedFilterState = stored?.filter_state || filterState.value || initialFilterState || ''
    const resolvedCompareSkus = normalizeProductSkus(response.value.products)
      .filter(sku => pendingCompareSkus.value.length === 0 || pendingCompareSkus.value.includes(sku))
      .slice(0, MAX_COMPARE_SKUS)

    if (!resolvedFilterThread || !resolvedFilterState || !resolvedCompareSkus.length) {
      const reason = !resolvedFilterThread
        ? 'missing filter_id from localStorage/route'
        : !resolvedFilterState
          ? 'missing filter_state from localStorage/route'
          : 'missing compare product skus'

      compareApiError.value = `Compare API skipped: ${reason}`
      return null
    }

    try {
      compareApiError.value = null
      await authStore.ensureSession()

      const payload: FilterCompareRequest = {
        filter_id: resolvedFilterThread,
        filter_thread: null,
        filter_state: resolvedFilterState,
        session_id: authStore.sessionId,
        customer_id: pendingCustomerId.value,
        page: 'filter',
        compare_skus: resolvedCompareSkus
      }

      const siteFetch = useNuxtApp().$siteFetch as <T>(
        url: string,
        options: {
          method: string
          body: FilterCompareRequest
          headers: Record<string, string>
          credentials: 'include'
        }
      ) => Promise<T>

      const apiResponse = await siteFetch<FilterCompareResponse>(`${config.public.tgptBaseUrl}${URL_CONFIG.AGENTIC_FILTER_COMPARE_ENDPOINT}`, {
        method: 'POST',
        body: payload,
        headers: await getHeaders(),
        credentials: 'include'
      })

      compareResponse.value = {
        ...structuredClone(EMPTY_COMPARE_RESPONSE),
        ...apiResponse,
        products: apiResponse?.products || [],
        comparison: apiResponse?.comparison || []
      }
      hasCompareResponse.value = true

      return compareResponse.value
    } catch (err: any) {
      const status = err?.statusCode || err?.response?.status || err?.status || 500
      const code = getErrorCode(err)
      const message = getErrorMessage(status, code)

      console.error('[useAgenticProductQuery] compare API failed', {
        status,
        code,
        activeFilterId: activeFilterId.value,
        filterState: filterState.value,
        endpoint: `${config.public.tgptBaseUrl}${URL_CONFIG.AGENTIC_FILTER_COMPARE_ENDPOINT}`
      })

      compareApiError.value = message
      toastStore.publish(message, 'error')
      throw err
    }
  }

  const { data: queryData, pending: isFiltering, error: queryError, refresh: refreshFilterQuery } = useAsyncData<FilterQueryResponse | null>(
    `agentic-product-query-${Date.now()}`,
    fetchFilterQuery,
    {
      server: true,
      lazy: false,
      immediate: false,
      default: () => null,
      watch: []
    }
  )


  const {
    data: compareData,
    pending: isComparing,
    error: compareError,
    refresh: refreshCompareQuery
  } = useAsyncData<FilterCompareResponse | null>(
    `agentic-product-compare-${Date.now()}`,
    fetchCompareQuery,
    {
      server: true,
      lazy: false,
      immediate: false,
      default: () => null,
      watch: []
    }
  )


  const runFilterQuery = async ( filterKey: string | undefined, operation: FilterOperation,nextAppliedFilters?: string[]) => {
    const stored = getAgenticFilterStorage()
    const currentStoredFilters = stored?.agenticfilters?.length ? normalizeFilterList(stored.agenticfilters) : appliedFilters.value
    const resolvedAppliedFilters = normalizeFilterList( nextAppliedFilters ?? getNextAppliedFilters(filterKey,operation) ?? currentStoredFilters)

    if (isFiltering.value) {
      apiError.value = 'Query API skipped: filter request already in progress'
      return null
    } 
    if (filterKey) {
      activeFilterKey.value =
        normalizeFilterValue(
          filterKey
        )
    }
    pendingOperation.value = operation
    pendingAppliedFilters.value = resolvedAppliedFilters

    updateAgenticFilterStorage({ filter_id: activeFilterId.value, filter_state: filterState.value, agenticfilters: resolvedAppliedFilters, filterkey: activeFilterKey.value})
    await refreshFilterQuery({ dedupe: 'cancel' })

    if (queryError.value) {
      throw queryError.value
    }

    return queryData.value
  }

  const addFilter = async (filterKey?: string) => {
    if (!activeFilterKey.value) return null

    const nextAppliedFilters = getNextAppliedFilters(filterKey, 'ADD')
    if (filterKey && isSameFilterList(nextAppliedFilters, normalizeFilterList(appliedFilters.value))) return null

    return runFilterQuery(filterKey, 'ADD', nextAppliedFilters)
  }

  const revertFilter = async (filterKey?: string) => {
    if (!activeFilterKey.value || !appliedFilters.value.length) return null

    const normalizedFilterKey = filterKey ? normalizeFilterValue(filterKey) : ''

    const nextAppliedFilters = normalizedFilterKey
      ? appliedFilters.value.slice(0, appliedFilters.value.lastIndexOf(normalizedFilterKey) + 1)
      : appliedFilters.value.slice(0, -1)

    if (nextAppliedFilters.length === appliedFilters.value.length) return null

    return runFilterQuery(normalizedFilterKey || undefined, 'REVERT', nextAppliedFilters)
  }


  const runCompareQuery = async (customerId = '', compareSkus: string[] = []) => {
    if (isComparing.value) {
      compareApiError.value = 'Compare API skipped: compare request already in progress'
      return null
    }
    
    compareResponse.value = structuredClone(EMPTY_COMPARE_RESPONSE)
    hasCompareResponse.value = false
    pendingCustomerId.value = customerId
    pendingCompareSkus.value = normalizeFilterList(compareSkus).slice(0, MAX_COMPARE_SKUS)

    await refreshCompareQuery({ dedupe: 'cancel' })

    if (compareError.value) {
      throw compareError.value
    }

    return compareData.value
  }
  return {
    response,
    compareResponse,
    filterState,
    appliedFilters,
    isFiltering,
    isComparing,
    hasQueryResponse,
    hasCompareResponse,
    apiError,
    compareApiError,
    hasValidFilterId,
    setInitContext,
    addFilter,
    revertFilter,
    runCompareQuery,
    normalizeFilterList,
    normalizeProductSkus,
    updateAgenticFilterStorage,
    syncFromAgenticFilterStorage
  }
}
